import { CART_STORE_KEY, CHECKOUT_STORE_KEY, VALIDATION_STORE_KEY } from '@woocommerce/block-data';
import { getSetting } from '@woocommerce/settings';
import apiFetch from '@wordpress/api-fetch';
import { useDispatch, useSelect } from '@wordpress/data';
import { useEffect, useState } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import { addQueryArgs } from '@wordpress/url';
import Dropdown from './selectors/dropdown/Dropdown';
import Modal from './selectors/modal/Modal';

/**
 * @param {ServicePointSelectorInputArguments} param0
 */
export default function ServicePointSelector({ shippingPackage, checkoutExtensionData, displayPackageTitle = false }) {
  const selectedRate = shippingPackage?.shipping_rates?.find((rate) => rate.selected);

  if (!selectedRate) {
    return null;
  }

  if (selectedRate.method_id !== 'shipmondo') {
    return null;
  }

  // Check if selected shipping rate is a service point delivery
  const isServicePointDelivery =
    typeof selectedRate.meta_data.find((e) => e.key === 'is_service_point_delivery' && e.value) !== 'undefined';

  if (!isServicePointDelivery) {
    return null;
  }

  /** @type {typedUseState<ShipmondoServicePoint[]>} */
  const [servicePoints, setServicePoints] = useState([]);

  const [isSelectorOpen, setIsSelectorOpen] = useState(false);

  // Selected service point - used to check if user has selected a service point and not just used the first on the list
  const [selectedServicePoint, setSelectedServicePoint] = useState(
    sessionStorage.getItem('shipmondo_selected_service_point') ?? null
  );

  /** @type {typedUseState<ShipmondoServicePoint | null>} */
  const [servicePoint, setServicePoint] = useState(null);

  /**
   * @param {ShipmondoServicePoint} servicePoint
   */
  function selectServicePoint(servicePoint) {
    setSelectedServicePoint(servicePoint.id);
    setServicePoint(servicePoint);
    setIsSelectorOpen(false);
  }

  useEffect(() => {
    if (selectedServicePoint) {
      sessionStorage.setItem('shipmondo_selected_service_point', selectedServicePoint);
    }
  }, [selectedServicePoint]);

  const { setValidationErrors, clearValidationError } = useDispatch(VALIDATION_STORE_KEY);

  useEffect(() => {
    if (!isServicePointDelivery || servicePoint) {
      clearValidationError('shipmondo_service_point');
    } else {
      setValidationErrors({
        shipmondo_service_point: {
          message: __('Please select a service point', 'shipmondo-for-woocommerce'),
          hidden: true,
        },
      });
    }
  }, [servicePoint]);

  const { getValidationError } = useSelect((select) => select(VALIDATION_STORE_KEY), []);

  const validationError = getValidationError('shipmondo_service_point');

  const [apiLoader, setApiLoader] = useState(false);

  const toggleSelector = () => {
    if (servicePoints.length > 0) {
      setIsSelectorOpen(!isSelectorOpen);
    } else {
      setIsSelectorOpen(false);
    }
  };

  // Get Service point from the extension data
  const extensionData = useSelect((select) => select(CHECKOUT_STORE_KEY).getExtensionData(), []);

  const { setExtensionData } = checkoutExtensionData;

  function buildServicePointRequestInput() {
    let carrierCode = '';
    let productCode = '';
    /** @type {string[]} */
    let servicePointTypes = [];

    selectedRate?.meta_data?.forEach((e) => {
      switch (e.key) {
        case 'shipping_agent':
          carrierCode = e.value;
          break;
        case 'product_code':
          productCode = e.value;
          break;
        case 'service_point_types':
          servicePointTypes = e.value;
          break;
        default:
          break;
      }
    });

    return {
      carrier_code: carrierCode,
      product_code: productCode,
      service_point_types: servicePointTypes,
      country_code: shippingPackage?.destination?.country ?? '',
      zipcode: shippingPackage?.destination?.postcode ?? '',
      city: shippingPackage?.destination?.city ?? '',
      address: shippingPackage?.destination?.address_1 ?? '',
    };
  }

  async function getServicePoints() {
    if (!isServicePointDelivery) {
      setServicePoints([]);
      return;
    }

    setApiLoader(true);

    return apiFetch({
      path: addQueryArgs('/shipmondo/v1/service-points', buildServicePointRequestInput()),
      method: 'GET',
      // Disables automatic response body parsing
      parse: false,
    }).then(async (response) => {
      if (response.status >= 400) {
        throw new Error('Received error status code: ' + response.status);
      }

      /** @type {ShipmondoServicePoint[]} */
      const body = await response.json();

      setServicePoints(body);
      setApiLoader(false);
    });
  }

  useEffect(() => {
    getServicePoints();
  }, [shippingPackage]);

  // If shipping rate is being selected, show loader
  const isShippingRateBeingSelected = useSelect(
    (select) => select(CART_STORE_KEY).isShippingRateBeingSelected(),
    false
  );

  useEffect(() => {
    setApiLoader(isShippingRateBeingSelected);
  }, [isShippingRateBeingSelected]);

  // Close selector if the loader is active
  useEffect(() => {
    if (apiLoader) {
      setIsSelectorOpen(false);
    }
  }, [apiLoader]);

  useEffect(() => {
    const foundServicePoint = servicePoints.find((e) => selectedServicePoint === e.id);

    if (selectedServicePoint && foundServicePoint) {
      setServicePoint(foundServicePoint);
      return;
    }

    if (
      extensionData &&
      extensionData[shippingPackage.package_id] &&
      typeof servicePoints.find((e) => extensionData[shippingPackage.package_id].id === e.id) !== 'undefined'
    ) {
      setServicePoint(extensionData[shippingPackage.package_id]);
      return;
    }

    setServicePoint(servicePoints[0] ?? null);
  }, [servicePoints]);

  // Save selected service point to local storage when changed
  useEffect(() => {
    if (servicePoint) {
      setExtensionData('shipmondo', 'selected_service_points', {
        ...(extensionData?.shipmondo?.selected_service_points || {}),
        [shippingPackage.package_id]: servicePoint,
      });
    }
  }, [servicePoint]);

  const settings = getSetting('shipmondo-service-point-selector_data');

  const selectorType = settings.selector_type ?? 'dropdown';

  let selector = null;

  if (selectorType === 'dropdown') {
    selector = (
      <Dropdown servicePoints={servicePoints} selectedServicePoint={servicePoint} onSelect={selectServicePoint} />
    );
  } else if (selectorType === 'modal') {
    selector = (
      <Modal
        servicePoints={servicePoints}
        selectedServicePoint={servicePoint}
        onSelect={selectServicePoint}
        onClose={() => setIsSelectorOpen(false)}
        mapsSettings={settings.maps_settings}
      />
    );
  }

  return (
    <div className={'shipmondo_service_point_selection'}>
      <h3 className={'service_point_title'}>
        {__('Service Point', 'shipmondo-for-woocommerce')}
        {displayPackageTitle ? ' - ' + shippingPackage.name : null}
      </h3>

      <div
        className={
          'selected_service_point service_point ' +
          selectorType +
          (apiLoader ? ' loading' : '') +
          (isSelectorOpen ? ' selector_open' : '') +
          (servicePoints.length === 0 ? ' no_service_point' : '') +
          (!validationError || validationError.hidden ? '' : ' has-error')
        }
        onClick={toggleSelector}
      >
        {servicePoint ? (
          <>
            <div className={'header'}>
              <span className={'name'}>{servicePoint.name}</span>
              <span className={'rate_name'}>{selectedRate.name}</span>
            </div>

            <div className={'location'}>
              <div className={'address_info'}>
                {sprintf(
                  // translators: %1$s: Address, %2$s: Zip Code, %3$s: City
                  __('%1$s, %2$s %3$s', 'shipmondo-for-woocommerce'),
                  servicePoint.address,
                  servicePoint.zipcode,
                  servicePoint.city
                )}
              </div>

              <div className={'distance'}>
                {parseFloat((servicePoint.distance / 1000).toFixed(2))} {__('km', 'shipmondo-for-woocommerce')}
              </div>
            </div>
          </>
        ) : (
          <>
            {shippingPackage.destination.postcode === '' ? (
              <>
                {__('Please enter the shipping address to see available service points', 'shipmondo-for-woocommerce')}
              </>
            ) : (
              <>{__('No service point available', 'shipmondo-for-woocommerce')}</>
            )}
          </>
        )}
      </div>
      {servicePoints && isSelectorOpen ? selector : null}
      {!validationError || validationError.hidden ? null : (
        <div className={'wc-block-components-validation-error'}>
          <p>{validationError?.message}</p>
        </div>
      )}
      <div className={'powered_by_shipmondo'}>
        <p>{__('Powered by Shipmondo', 'shipmondo-for-woocommerce')}</p>
      </div>
    </div>
  );
}
